import React, { forwardRef, useState, useImperativeHandle, useRef } from 'react';
import { TitlelessDialogBox, useDialog } from '../../comps/dialog';
import { Button } from '@headlessui/react'
import { Segment } from '../../state/models';
import { Checkbox } from '../../comps/checkbox';
import { ls } from '../../locale';
import btnStyles from '../../styles/btn.module.scss';


export type FlightSelectHandler = {
  show(segments: Segment[], selected: number[], title?: string, message?: string, notes?: string): Promise<number[]>
}

export const FlightSelect = forwardRef<FlightSelectHandler>((props, ref) => {

  const [notes, setNotes] = useState<string | undefined>()
  const [segments, setSegments] = useState<Segment[]>([])
  const [curSelected, setCurSelected] = useState<number[]>([])
  const [preSelected, setPreSelected] = useState<number[]>([])

  // TODO: use a better way to store the selected flights
  const selection = useRef<number[]>([])

  const dialog = useDialog("Change Flight", "Choose the flight you want to change");

  function show(segments: Segment[], selected: number[] = [], title?: string, message?: string, notes?: string): Promise<number[]> {

    setSegments(segments)
    setCurSelected(selected)
    setPreSelected(selected)
    setNotes(notes);

    selection.current = selected

    return new Promise<number[]>((resolve, reject) => {
      dialog.show(message, title)
        .then(() => {
          resolve(selection.current)
        })
        .catch(() => reject(false))
    })
  }

  // expose the show method to the parent component
  useImperativeHandle(ref, () => ({ show }))

  const select = (segment: Segment) => {
    const flightId = segment.logicalFlightId

    if (!curSelected.includes(flightId)) {
      selection.current = [...curSelected, flightId]
      setCurSelected(selection.current)
      return
    }

    // we don't remove pre selected flights
    if (preSelected.includes(flightId)) return;

    selection.current = curSelected.filter(id => id !== flightId)
    setCurSelected(selection.current)
  }

  const isDisabled = (segment: Segment) => {
    return preSelected.length > 0 && preSelected.includes(segment.logicalFlightId)
  }

  return (
    <TitlelessDialogBox open={dialog.open} onClose={dialog.close} panelClass="tw-max-w-sm tw-p-5">
      <div className="tw-my-4">
        <div className="tw-flex tw-flex-col tw-items-center tw-mb-6">
          <div><i className="picon picon-action" /></div>
          <div className="tw-px-2 sa-text-blue tw-text-lg">{dialog.title}</div>
        </div>
        <p className="sa-text-blue">{dialog.message}</p>
        <div className="tw-flex tw-flex-col tw-gap-2 tw-px-2">
          {segments.map(segment => (
            <Checkbox key={segment.logicalFlightId}
              checked={curSelected.includes(segment.logicalFlightId)}
              disabled={typeof isDisabled === 'function' ? isDisabled(segment) : false}
              onChange={() => select(segment)}
            >
              <span className='tw-font-bold sa-text-green'>{`${segment.originCity} - ${segment.destinationCity}`}</span>
            </Checkbox>
          ))}
        </div>
        {notes && <p className="tw-mt-4 tw-text-xs">{notes}</p>}
      </div>
      <div className="tw-grid tw-grid-flow-col tw-justify-stretch tw-gap-2 tw-mt-8">
        <Button className={btnStyles.default} onClick={() => dialog.close(false)}>{ls.t('Button.Cancel')}</Button>
        <Button className={btnStyles.primary} onClick={() => dialog.close(true)}>Select</Button>
      </div>
    </TitlelessDialogBox>
  )
})